import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get("session");
    if (s === "idle") toast.info("Sesi berakhir. Silakan masuk kembali.");
    else if (s === "expired") toast.info("Sesi Anda kedaluwarsa. Silakan masuk kembali.");
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Selamat datang!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={TESTIDS.loginPage} className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* LEFT: Hero */}
      <div className="relative hidden lg:block grain-overlay overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1504297050568-910d24c426d3?auto=format&fit=crop&w=1400&q=80"
          alt="Kantor modern"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-klein" />
            <div className="font-display font-bold tracking-tight text-xl">ABSENSI-EPISI</div>
          </div>
          <div className="max-w-md">
            <div className="label-tiny text-white/70 mb-4">Workforce Presence · v1.1</div>
            <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tighter leading-[0.95]">
              Kehadiran yang<br />terverifikasi.
            </h1>
            <p className="mt-6 text-white/80 text-base leading-relaxed">
              Sistem absensi berbasis geolokasi & swafoto dengan approval cuti/lembur berjenjang.
              Aman, transparan, mudah dipakai.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 border-t border-white/20 pt-6">
            <div>
              <div className="label-tiny text-white/60">Multi Kantor</div>
              <div className="font-display text-2xl font-bold mt-1">Ya</div>
            </div>
            <div>
              <div className="label-tiny text-white/60">WFA</div>
              <div className="font-display text-2xl font-bold mt-1">Didukung</div>
            </div>
            <div>
              <div className="label-tiny text-white/60">Approval</div>
              <div className="font-display text-2xl font-bold mt-1">Berjenjang</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: Form */}
      <div className="flex items-center justify-start px-8 lg:px-16 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-md">
          <div className="label-tiny">Masuk ke Sistem</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-4">
            Selamat datang.
          </h2>
          <p className="mt-4 text-gray-600 text-base leading-relaxed">
            Gunakan email dan password yang diberikan oleh Admin Sucofindo Episi.
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <div className="label-tiny mb-2">Email</div>
              <input
                data-testid={TESTIDS.loginEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="anda@sucofindoepisi.co.id"
                className="w-full border border-black/20 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-klein"
              />
            </div>
            <div>
              <div className="label-tiny mb-2">Password</div>
              <input
                data-testid={TESTIDS.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full border border-black/20 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-klein"
              />
            </div>
          </div>

          <button
            data-testid={TESTIDS.loginSubmit}
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-klein text-white px-6 py-4 hover:bg-black transition-colors font-semibold disabled:bg-gray-300"
          >
            <LogIn className="w-5 h-5" />
            {loading ? "Memproses..." : "Masuk"}
          </button>

          <p className="mt-16 text-xs text-gray-400">
            © {new Date().getFullYear()} Absensi-EPISI · @agung_gumilar
          </p>
        </form>
      </div>
    </div>
  );
}
